import posthog from 'posthog-js';
import { isPosthogInitialized, persistedDistinctId } from '@/plugins/posthog';
import { PRODUCTION } from '@/store/constants';

// Enable PostHog tracking in dev by adding ?posthog_debug=true to URL
const isPosthogDebug = () => {
    if (typeof window !== 'undefined') {
        return new URLSearchParams(window.location.search).get('posthog_debug') === 'true';
    }
    return false;
};

/**
 * The distinct id this browser reports events under.
 *
 * Report PDFs are produced server-side, so the backend is what emits
 * `report_generated`. Sending this along with the request lets that event land
 * on the same PostHog person as everything captured here — without it every
 * anonymous report shares one "anonymous" id and unique-user counts collapse.
 *
 * Prefers the live value; falls back to what posthog-js persisted, which is
 * also what the feature-flag bootstrap reads before init.
 */
export const getTrackingDistinctId = () => {
    try {
        if (isPosthogInitialized()) {
            return posthog.get_distinct_id() || persistedDistinctId();
        }
        return persistedDistinctId();
    } catch (e) {
        return null;
    }
};

// Helper to get current URL - safe fallback
export const getCurrentUrl = () => {
    try {
        return window.location.href;
    } catch (e) {
        return null;
    }
};

// Helper to get page title - safe fallback
export const getPageTitle = () => {
    try {
        return document.title;
    } catch (e) {
        return null;
    }
};

// Helper to extract UTM parameters from URL - safe fallback
export const getUtmParams = () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            utm_source: urlParams.get('utm_source') || null,
            utm_campaign: urlParams.get('utm_campaign') || null,
            utm_medium: urlParams.get('utm_medium') || null,
            utm_content: urlParams.get('utm_content') || null,
            utm_term: urlParams.get('utm_term') || null,
        };
    } catch (e) {
        return {
            utm_source: null,
            utm_campaign: null,
            utm_medium: null,
            utm_content: null,
            utm_term: null,
        };
    }
};

// Get PostHog instance directly from the library (it's a singleton)
const getPosthog = () => posthog;

// Helper to get store instance
const getStore = () => {
    if (typeof window !== 'undefined' && window.Vue?.prototype?.$store) {
        return window.Vue.prototype.$store;
    }
    return null;
};

// Helper to get user type from store - safe fallback
export const getUserType = () => {
    try {
        const store = getStore();
        if (store) {
            return store.getters.isAdvisor ? 'advisor' : 'client';
        }
        return null;
    } catch (e) {
        return null;
    }
};

// Helper to get user data from store - safe fallback
export const getUserData = () => {
    try {
        const store = getStore();
        if (store) {
            const user = store.getters.user || {};
            return {
                first_name: user.first_name || null,
                last_name: user.last_name || null,
                email: user.email || null,
                user_type: getUserType(),
            };
        }
        return {
            first_name: null,
            last_name: null,
            email: null,
            user_type: null,
        };
    } catch (e) {
        return {
            first_name: null,
            last_name: null,
            email: null,
            user_type: null,
        };
    }
};

// Main tracking function - silently fails on any error
export const trackPosthogEvent = (event, data = {}) => {
    try {
        const userType = getUserType();
        const eventData = {
            ...data,
            user_type: userType,
        };

        const shouldTrack = PRODUCTION || isPosthogDebug();

        if (shouldTrack) {
            const posthog = getPosthog();
            if (posthog) {
                posthog.capture(event, eventData);
            }
        }
    } catch (e) {
        // Silent failure - tracking should never break user flow
    }
};

// Track page view with enhanced properties
export const trackPageView = (routeInfo = {}) => {
    trackPosthogEvent('App Page Viewed', {
        current_url: getCurrentUrl(),
        page_title: getPageTitle(),
        route_name: routeInfo.name || null,
        route_path: routeInfo.path || null,
        ...getUtmParams(),
    });
};

// Track button click
export const trackButtonClick = (buttonData = {}) => {
    const userData = getUserData();
    trackPosthogEvent('App Button Clicked', {
        current_url: getCurrentUrl(),
        target_url: buttonData.targetUrl || null, // External URL, internal path, or null for modals
        button_title: buttonData.title || null,
        button_location: buttonData.location || null,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
    });
};

// Track account registration
export const trackAccountRegistered = (registrationData = {}) => {
    trackPosthogEvent('Account Registered', {
        account_registration_time: new Date().toISOString(),
        where_did_you_hear_about_us: registrationData.referral_source || null,
        method: registrationData.method || 'email',
    });
};

// Track user login
export const trackUserLoggedIn = (loginData = {}) => {
    trackPosthogEvent('User Logged In', {
        email: loginData.email || null,
        last_logged_in_time: new Date().toISOString(),
        login_method: loginData.method || null,
    });
};

// Track guided planning initiated
export const trackGuidedPlanningInitiated = (taxStrategy = null) => {
    trackPosthogEvent('Guided Planning Initiated', {
        tax_strategy: taxStrategy,
    });
};

// Track guided planning stage
export const trackGuidedPlanningStage = (stageTitle, taxStrategy = null) => {
    trackPosthogEvent(`Guided Planning Stage ${stageTitle}`, {
        tax_strategy: taxStrategy,
    });
};

// Funnel entry: a self-declared advisor enters the self-registration branch.
// Everything below is measured against this — without it there's no denominator
// for "declared advisor → workspace created".
export const trackAdvisorOnboardingStarted = () => {
    trackPosthogEvent('Advisor Onboarding Started', {
        current_url: getCurrentUrl(),
    });
};

// How the email-domain lookup resolved. Drives the GTM signal for which firms
// to chase for a domain opt-in ("matched but not opted in").
export const trackAdvisorFirmMatchResolved = ({ outcome, partnerUuid = null, isOverride = false }) => {
    trackPosthogEvent('Advisor Firm Match Resolved', {
        // 'joinable' | 'not_opted_in' | 'no_match'
        outcome,
        partner_uuid: partnerUuid,
        // true when the match came from a code-verified alternate work email
        // (the workspace finder) rather than the account email's domain.
        is_override: isOverride,
        current_url: getCurrentUrl(),
    });
};

// The workspace finder's ownership gate: a 6-digit code was emailed to an
// alternate work address. Denominator for the verification funnel.
export const trackAdvisorWorkEmailCodeRequested = ({ isResend = false } = {}) => {
    trackPosthogEvent('Advisor Work Email Code Requested', {
        is_resend: isResend,
        current_url: getCurrentUrl(),
    });
};

// Ownership proven (the emailed code was accepted). The match outcome itself
// is carried by Advisor Firm Match Resolved (is_override=true), fired at the
// same moment — this event exists so the request → verified funnel has a
// clean success step.
export const trackAdvisorWorkEmailVerified = ({ matched = false, partnerUuid = null } = {}) => {
    trackPosthogEvent('Advisor Work Email Verified', {
        matched,
        partner_uuid: partnerUuid,
        current_url: getCurrentUrl(),
    });
};

// Track an advisor self-registering a workspace (starts the 30-day trial)
export const trackAdvisorWorkspaceCreated = (workspace = {}) => {
    trackPosthogEvent('Advisor Workspace Created', {
        workspace_uuid: workspace.uuid || null,
        // true while the subdomain is reserved-but-not-served (pre white-label).
        is_reserved: workspace.is_reserved ?? null,
        current_url: getCurrentUrl(),
    });
};

// Track an advisor joining an existing domain-matched firm workspace (no trial)
export const trackAdvisorWorkspaceJoined = (workspace = {}) => {
    trackPosthogEvent('Advisor Workspace Joined', {
        workspace_uuid: workspace.uuid || null,
        current_url: getCurrentUrl(),
    });
};

// Track an advisor asking for a consultation — the trial's conversion milestone
export const trackAdvisorConsultationRequested = (trial = {}) => {
    trackPosthogEvent('Advisor Consultation Requested', {
        // The API exposes the lifecycle value as `state` ('active' | 'expired'
        // | 'converted') — `.status` was recording null for every event.
        trial_status: trial.state || null,
        days_left: trial.days_left ?? null,
        current_url: getCurrentUrl(),
    });
};

// Track solar calculator initiated
export const trackSolarCalculatorInitiated = () => {
    trackPosthogEvent('Solar Calculator Initiated');
};

// Track solar calculator stage
export const trackSolarCalculatorStage = stageTitle => {
    trackPosthogEvent(`Solar Calculator Stage ${stageTitle}`);
};

// Track onboarding initiated
export const trackOnboardingInitiated = (onboardingData = {}) => {
    trackPosthogEvent('Onboarding Initiated', {
        tax_strategy: onboardingData.product_label || null,
        client_email: onboardingData.client_email || null,
    });
};

const onboardingStepProperties = ({
    product,
    onboardingUuid,
    stepKey,
    stepIndex,
    stepTotal,
    stepTitle,
    actorRole,
} = {}) => ({
    product: product || null,
    onboarding_uuid: onboardingUuid || null,
    step_key: stepKey || null,
    step_index: stepIndex ?? null,
    step_total: stepTotal ?? null,
    step_title: stepTitle || null,
    actor_role: actorRole || null,
});

export const trackOnboardingStepViewed = (context = {}) => {
    trackPosthogEvent('Onboarding Step Viewed', {
        ...onboardingStepProperties(context),
        entry_direction: context.entryDirection || null,
        from_step_key: context.fromStepKey || null,
        from_step_index: context.fromStepIndex ?? null,
        seconds_on_previous_step: context.secondsOnPreviousStep ?? null,
        is_resumed: Boolean(context.isResumed),
        furthest_step_index: context.furthestStepIndex ?? null,
    });
};

export const trackOnboardingStepCompleted = (context = {}) => {
    trackPosthogEvent('Onboarding Step Completed', {
        ...onboardingStepProperties(context),
        seconds_on_step: context.secondsOnStep ?? null,
        seconds_visible: context.secondsVisible ?? null,
        submit_attempts: context.submitAttempts ?? null,
        is_edit_from_review: Boolean(context.isEditFromReview),
    });
};

export const trackOnboardingStepBlocked = (context = {}) => {
    trackPosthogEvent('Onboarding Step Blocked', {
        ...onboardingStepProperties(context),
        seconds_blocked: context.secondsBlocked ?? null,
        seconds_on_step: context.secondsOnStep ?? null,
    });
};

export const trackOnboardingStepSubmitFailed = (context = {}) => {
    trackPosthogEvent('Onboarding Step Submit Failed', {
        ...onboardingStepProperties(context),
        status: context.status ?? null,
        error_kind: context.errorKind || null,
        attempt: context.attempt ?? null,
        action: context.action || null,
        source: context.source || null,
    });
};

export const trackOnboardingError = (context = {}) => {
    trackPosthogEvent('Onboarding Error', {
        ...onboardingStepProperties(context),
        error_name: context.errorName || null,
        error_message: context.errorMessage || null,
        vue_info: context.vueInfo || null,
        source: context.source || 'vue_error',
    });
};

export const trackOnboardingReviewEditClicked = (context = {}) => {
    trackPosthogEvent('Onboarding Review Edit Clicked', {
        ...onboardingStepProperties(context),
    });
};

export const trackOnboardingExited = (context = {}) => {
    trackPosthogEvent('Onboarding Exited', {
        ...onboardingStepProperties(context),
        progress_pct: context.progressPct ?? null,
        furthest_step_index: context.furthestStepIndex ?? null,
        seconds_on_step: context.secondsOnStep ?? null,
        seconds_in_wizard: context.secondsInWizard ?? null,
        completed_step_count: context.completedStepCount ?? null,
    });
};

export const trackOnboardingIntakeSubmitted = (context = {}) => {
    trackPosthogEvent('Onboarding Intake Submitted', {
        ...onboardingStepProperties(context),
        seconds_in_wizard: context.secondsInWizard ?? null,
        review_edit_count: context.reviewEditCount ?? null,
        submit_failure_count: context.submitFailureCount ?? null,
    });
};

export const trackDownloadReport = (reportData = {}) => {
    trackPosthogEvent('Download Report', {
        email: reportData.email || null,
        current_page_url: getCurrentUrl(),
        client_email: reportData.client_email || null,
        report_type: reportData.type || null,
    });
};

// Track added client (when a client registers via advisor's invitation link)
export const trackAddedClient = (clientData = {}) => {
    trackPosthogEvent('Added Client', {
        client_email: clientData.clientEmail || null,
        advisor_email: clientData.advisorEmail || null,
    });
};

// Every event is tagged tracking_source: 'product' by a super property
// registered at init (plugins/posthog.js); no per-event tagging needed.
// Growth-surface events default to 'growth' at PostHog ingestion — see
// docs/features/product-analytics.md.

export const trackAccountActivated = () => {
    trackPosthogEvent('Account Activated', {
        activation_time: new Date().toISOString(),
    });
};

export const trackCalculatorOpened = calculatorType => {
    trackPosthogEvent('Calculator Opened', {
        calculator_type: calculatorType,
        current_url: getCurrentUrl(),
    });
};

// --- Gift Tax Questionnaire (ENG-2748) ---
// The questionnaire holds SSNs/financial detail — NEVER put answer payloads
// here. Only ids, counts, and section keys are allowed. `tracking_source`
// is owned by the posthog-js super property (see plugins/posthog.js); don't set it.
export const trackGiftTaxQuestionnaireStarted = () => {
    trackPosthogEvent('Gift Tax Questionnaire Started', {
        current_url: getCurrentUrl(),
    });
};

export const trackGiftTaxQuestionnaireStepCompleted = sectionKey => {
    trackPosthogEvent('Gift Tax Questionnaire Step Completed', {
        section_key: sectionKey,
        current_url: getCurrentUrl(),
    });
};

export const trackGiftTaxQuestionnaireSubmitted = ({ taxYear, giftsCount, filesCount } = {}) => {
    trackPosthogEvent('Gift Tax Questionnaire Submitted', {
        tax_year: taxYear ?? null,
        gifts_count: giftsCount ?? null,
        files_count: filesCount ?? null,
    });
};

export const trackGiftTaxQuestionnaireEdited = () => {
    trackPosthogEvent('Gift Tax Questionnaire Edited', {
        current_url: getCurrentUrl(),
    });
};

// --- Scenarios (ENG-2741) ---
// Answers the two questions from the scenarios product review: do advisors actually save
// and assign runs, and is the leave-the-calculator prompt earning its interruption.
// `calculator_type` is the uppercase label (CRUT / GRAT / …) so these join
// 'Calculator Opened'. NEVER put calculator inputs, dollar figures or the run
// fingerprint in here — only ids, types and booleans.

export const trackScenarioSaved = ({ calculatorType, assignedToClient, source } = {}) => {
    trackPosthogEvent('Scenario Saved', {
        calculator_type: calculatorType ?? null,
        assigned_to_client: Boolean(assignedToClient),
        // 'actions' (the Save button) | 'leave_prompt'
        source: source ?? null,
    });
};

export const trackScenarioAssigned = ({ calculatorType, isNewClient, source } = {}) => {
    trackPosthogEvent('Scenario Assigned', {
        calculator_type: calculatorType ?? null,
        is_new_client: Boolean(isNewClient),
        source: source ?? null,
    });
};

export const trackScenarioOpened = ({ calculatorType, source } = {}) => {
    trackPosthogEvent('Scenario Opened', {
        calculator_type: calculatorType ?? null,
        // 'card' | 'list' | 'drawer'
        source: source ?? null,
    });
};

export const trackScenarioReportOpened = ({ calculatorType, reportType, source } = {}) => {
    // A report PDF attached to a saved run was opened from a scenario surface (ENG-2918).
    trackPosthogEvent('Scenario Report Opened', {
        calculator_type: calculatorType ?? null,
        // ReportRequest.report_type — crut_full, qsbs_full, …
        report_type: reportType ?? null,
        // 'card' | 'list' | 'drawer' | 'calculator' (the calculator header's report control)
        source: source ?? null,
    });
};

export const trackScenarioReportGenerated = ({ calculatorType, trigger, succeeded } = {}) => {
    // The scenario's own report was (re)rendered from the saved run (ENG-2918).
    trackPosthogEvent('Scenario Report Generated', {
        calculator_type: calculatorType ?? null,
        // 'save' (right after a save left the report missing or outdated) | 'open' (someone
        // opened a missing or outdated report, which renders a fresh one first). The 'manual'
        // Generate action no longer exists.
        trigger: trigger ?? null,
        succeeded: Boolean(succeeded),
    });
};

export const trackScenarioLifecycleAction = (action, { calculatorType, previousStatus } = {}) => {
    // action: 'Shared' | 'Unshared' | 'Pinned' | 'Unpinned' | 'Archived' | 'Unarchived'
    //       | 'Renamed' | 'Deleted'
    trackPosthogEvent(`Scenario ${action}`, {
        calculator_type: calculatorType ?? null,
        // The status the run had BEFORE the action — that's the interesting half
        // ("archived from expiring" says the expiry nudge worked). Named explicitly
        // because reading it as the current status would be wrong.
        previous_status: previousStatus ?? null,
    });
};

export const trackSaveRunPromptShown = ({ calculatorType, destinationRoute, canAssign } = {}) => {
    trackPosthogEvent('Save Run Prompt Shown', {
        calculator_type: calculatorType ?? null,
        destination_route: destinationRoute ?? null,
        can_assign: Boolean(canAssign),
        current_url: getCurrentUrl(),
    });
};

export const trackSaveRunPromptSaved = ({ calculatorType, destinationRoute, assignedToClient, neverAskAgain } = {}) => {
    trackPosthogEvent('Save Run Prompt Saved', {
        calculator_type: calculatorType ?? null,
        destination_route: destinationRoute ?? null,
        assigned_to_client: Boolean(assignedToClient),
        never_ask_again: Boolean(neverAskAgain),
    });
};

export const trackSaveRunPromptDismissed = ({ calculatorType, destinationRoute, outcome, neverAskAgain } = {}) => {
    trackPosthogEvent('Save Run Prompt Dismissed', {
        calculator_type: calculatorType ?? null,
        destination_route: destinationRoute ?? null,
        // Both continue to the destination — the prompt is an offer, not a gate.
        // 'left' = chose "Leave without saving"; 'dismissed' = closed the dialog (X/Esc).
        outcome: outcome ?? null,
        never_ask_again: Boolean(neverAskAgain),
    });
};

// The denominator for the two above: without it you can't tell "nobody has unsaved runs"
// from "we're suppressing every prompt".
export const trackSaveRunPromptSuppressed = ({ calculatorType, reason } = {}) => {
    trackPosthogEvent('Save Run Prompt Suppressed', {
        calculator_type: calculatorType ?? null,
        // 'never_ask' | 'duplicate_run'
        reason: reason ?? null,
    });
};

const SAFE_PAYLOAD_KEYS = [
    'age',
    'state',
    'filing_status',
    'term',
    'stcg_exit_percentage',
    'annual_cap_gains_rate',
    'annual_dividends_rate',
    'annual_growth_rate',
    'annual_income_growth',
    'annual_asset_growth',
    'flip_year',
];

const sanitizePayload = payload => {
    if (!payload || typeof payload !== 'object') return {};
    const safe = {};
    for (const key of SAFE_PAYLOAD_KEYS) {
        if (key in payload) {
            safe[key] = payload[key];
        }
    }
    safe.field_count = Object.keys(payload).length;
    return safe;
};

export const trackCalculatorCalculated = (calculatorType, payload = {}) => {
    trackPosthogEvent('Calculator Calculated', {
        calculator_type: calculatorType,
        current_url: getCurrentUrl(),
        calculator_inputs: sanitizePayload(payload),
    });
};

export const trackAdvisoryPartnerHit = partnerName => {
    trackPosthogEvent('Advisory Partner Hit', {
        partner_name: partnerName,
    });
};

export const trackSetUpTrustClicked = productLabel => {
    trackPosthogEvent('Set Up Trust Clicked', {
        product_label: productLabel,
        current_url: getCurrentUrl(),
    });
};

// Advisor workspace management (ENG-2742). Page views and button clicks are already
// covered generically — these are the semantic moments: did a firm actually brand its
// workspace, does the dashboard drive anyone into the work it flags, and does the
// change log get used.
export const trackWorkspaceBrandingUpdated = (fields = []) => {
    trackPosthogEvent('Workspace Branding Updated', {
        fields,
        current_url: getCurrentUrl(),
    });
};

export const trackWorkspaceDashboardItemOpened = ({
    kind,
    step = null,
    daysIdle = null,
    openTaskCount = null,
} = {}) => {
    trackPosthogEvent('Workspace Dashboard Item Opened', {
        kind,
        step,
        days_idle: daysIdle,
        open_task_count: openTaskCount,
    });
};

export const trackProjectChangeLogOpened = ({ product = null, isClientView = false } = {}) => {
    trackPosthogEvent('Project Change Log Opened', {
        product,
        is_client_view: isClientView,
    });
};

export const trackPasswordChanged = () => {
    trackPosthogEvent('Password Changed', {
        changed_at: new Date().toISOString(),
    });
};

/*
 * Small-screen gate instrumentation (ENG-2840). Two events — the gate going up and
 * coming down — because those are the moments the generic layer can't express.
 *
 * Deliberately NOT an event per narrow pageview: that would duplicate `App Page Viewed`.
 * The question the rollout has to answer is "does anything actually get *done* at these
 * widths" — funnels completed, calculators run — and those events already exist. The
 * `viewport_band` super property (registered in plugins/posthog.js) tags every one of
 * them, so that analysis is a breakdown on existing series, not a new set of events.
 */

/**
 * The small-screen gate covered the app. `min_supported_width` records which floor was
 * in force, so the two flag cohorts stay separable in one series.
 */
export const trackSmallScreenGateShown = ({ width, height, minSupportedWidth, routeName, isPartner, canRotate }) => {
    trackPosthogEvent('Small Screen Gate Shown', {
        viewport_width: width,
        viewport_height: height,
        min_supported_width: minSupportedWidth,
        route_name: routeName || null,
        is_partner_tenant: Boolean(isPartner),
        // The gate offered this user a rotation rather than a different computer, which
        // splits the two populations the floor produces.
        can_rotate: Boolean(canRotate),
        current_url: getCurrentUrl(),
    });
};

/**
 * The gate came back down. `reason` separates the outcome we care about (`resized` — the
 * user widened the window, and went on to use the app) from merely landing on an exempt
 * route while still too narrow, or the flag being switched off under them (`flag-off`).
 * All three clear the gate; only the first is a recovery.
 */
export const trackSmallScreenGateLifted = ({ width, minSupportedWidth, routeName, reason, secondsBlocked }) => {
    trackPosthogEvent('Small Screen Gate Lifted', {
        viewport_width: width,
        min_supported_width: minSupportedWidth,
        route_name: routeName || null,
        reason,
        seconds_blocked: secondsBlocked,
        current_url: getCurrentUrl(),
    });
};

export const trackHamiltonMessageSent = ({ conversationId, intake, product, messageLength }) => {
    trackPosthogEvent('Hamilton Message Sent', {
        conversation_id: conversationId,
        onboarding_uuid: intake || null,
        product: product || null,
        is_bound: Boolean(intake),
        message_length: messageLength,
    });
};

export const trackHamiltonLimitReached = ({ conversationId, intake, product }) => {
    trackPosthogEvent('Hamilton Limit Reached', {
        conversation_id: conversationId || null,
        onboarding_uuid: intake || null,
        product: product || null,
        is_bound: Boolean(intake),
    });
};

export const trackEditRequestProposed = ({ conversationId, intake, product, toolCallId }) => {
    trackPosthogEvent('Edit Request Proposed', {
        conversation_id: conversationId,
        onboarding_uuid: intake || null,
        product: product || null,
        tool_call_id: toolCallId,
    });
};

export const trackEditRequestCancelled = ({ conversationId, intake, product, toolCallId }) => {
    trackPosthogEvent('Edit Request Cancelled', {
        conversation_id: conversationId,
        onboarding_uuid: intake || null,
        product: product || null,
        tool_call_id: toolCallId,
    });
};

export const trackEditRequestEdited = ({ editRequestUuid, intake, product }) => {
    trackPosthogEvent('Edit Request Edited', {
        edit_request_uuid: editRequestUuid,
        onboarding_uuid: intake,
        product: product || null,
    });
};

export const trackEditRequestWithdrawn = ({ editRequestUuid, intake, product }) => {
    trackPosthogEvent('Edit Request Withdrawn', {
        edit_request_uuid: editRequestUuid,
        onboarding_uuid: intake,
        product: product || null,
    });
};

// Enhanced identify function - silently fails on any error
export const identifyUser = (userId, userData = {}) => {
    try {
        // Person properties drive feature-flag rollout tiers; keep in sync with
        // the backend's valur.feature_flags._person_properties.
        const identifyData = {
            email: userData.email || null,
            user_type: userData.is_advisor ? 'advisor' : 'client',
            first_name: userData.first_name || null,
            last_name: userData.last_name || null,
            is_staff: Boolean(userData.is_staff),
            signup_method: userData.signup_method || null,
        };

        if (userData.advisor_role) {
            // Only when set — the backend omits the key for non-advisors, and
            // present-with-null flips PostHog "is (not) set" filters vs. absent.
            identifyData.advisor_role = userData.advisor_role;
        }

        // Identify whenever PostHog is initialized (staging runs in
        // flags-only mode and still need the identity for flag targeting).
        // Event capture stays production-gated separately (trackPosthogEvent).
        if (isPosthogInitialized()) {
            const posthog = getPosthog();
            if (posthog) {
                posthog.identify(userId, identifyData);
            }
        }
    } catch (e) {
        // Silent failure - tracking should never break user flow
    }
};

// Trust accounting (ENG-3000). No monetary values, names, or trigger text enter analytics.
const trustAccountingContext = context => ({
    trust_uuid: context.trust_uuid,
    trust_type: context.trust_type,
    view: context.view,
    data_state: context.data_state,
    demo: Boolean(context.demo),
});
export const trackTrustAccountingViewOpened = context =>
    trackPosthogEvent('Trust Accounting View Opened', trustAccountingContext(context));
export const trackTrustWealthViewed = context =>
    trackPosthogEvent('Trust Wealth Module Viewed', trustAccountingContext(context));
export const trackTrustWithdrawalAdjusted = context =>
    trackPosthogEvent('Trust Withdrawal Adjusted', {
        ...trustAccountingContext(context),
        control: context.control,
        year: context.year ?? null,
    });
export const trackTrustFlipMarked = context =>
    trackPosthogEvent('Trust Flip Marked Triggered', trustAccountingContext(context));
